import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Send } from 'lucide-react';

const FormSubmission = () => {
  const { appId } = useParams();
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [formData, setFormData] = useState({
    customer_id: '',
    location_id: '',
    technician_name: '',
    work_description: '',
    notes: '',
    signature: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchApp();
    fetchCustomers();
  }, [appId]);

  useEffect(() => {
    if (formData.customer_id) {
      fetchLocations(formData.customer_id);
    }
  }, [formData.customer_id]);

  const fetchApp = async () => {
    try {
      const response = await axios.get(`${API}/apps`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const selectedApp = response.data.find(a => a.id === appId);
      setApp(selectedApp);
    } catch (error) {
      toast.error('Failed to load app');
      navigate('/field');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data.filter(c => c.status === 'active'));
    } catch (error) {
      console.error('Failed to fetch customers');
    }
  };

  const fetchLocations = async (customerId) => {
    try {
      const response = await axios.get(`${API}/locations?customer_id=${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data);
    } catch (error) {
      console.error('Failed to fetch locations');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/submissions`, {
        app_id: appId,
        customer_id: formData.customer_id || null,
        location_id: formData.location_id || null,
        data: formData,
        photos: [],
        signature: formData.signature || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Form submitted successfully! / ¡Formulario enviado con éxito!');
      navigate('/field/history');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Submission failed / Error al enviar');
    } finally {
      setLoading(false);
    }
  };

  if (!app) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-slate-500">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <Button
        variant="outline"
        onClick={() => navigate('/field')}
        className="mb-4 bg-white"
        data-testid="back-to-apps-btn"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back / Volver
      </Button>

      <Card className="shadow-xl">
        <div className="h-4" style={{ backgroundColor: app.header_color }} />
        <CardHeader>
          <CardTitle className="text-2xl">{app.name} / {app.name_es}</CardTitle>
          <CardDescription>{app.description} / {app.description_es}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Customer Selection */}
            <div className="space-y-2">
              <Label htmlFor="customer">Customer / Cliente</Label>
              <select
                id="customer"
                data-testid="form-customer-select"
                className="w-full border rounded-md px-3 py-2"
                value={formData.customer_id}
                onChange={(e) => setFormData({ ...formData, customer_id: e.target.value, location_id: '' })}
              >
                <option value="">Select Customer / Seleccionar Cliente</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>{customer.name}</option>
                ))}
              </select>
            </div>

            {/* Location Selection */}
            {formData.customer_id && (
              <div className="space-y-2">
                <Label htmlFor="location">Location / Ubicación</Label>
                <select
                  id="location"
                  data-testid="form-location-select"
                  className="w-full border rounded-md px-3 py-2"
                  value={formData.location_id}
                  onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                >
                  <option value="">Select Location / Seleccionar Ubicación</option>
                  {locations.map(location => (
                    <option key={location.id} value={location.id}>{location.name} ({location.type})</option>
                  ))}
                </select>
              </div>
            )}

            {/* Technician Name */}
            <div className="space-y-2">
              <Label htmlFor="technician_name">Technician Name / Nombre del Técnico</Label>
              <Input
                id="technician_name"
                data-testid="form-technician-input"
                value={formData.technician_name}
                onChange={(e) => setFormData({ ...formData, technician_name: e.target.value })}
                required
              />
            </div>

            {/* Work Description */}
            <div className="space-y-2">
              <Label htmlFor="work_description">Work Description / Descripción del Trabajo</Label>
              <Textarea
                id="work_description"
                data-testid="form-work-description-input"
                value={formData.work_description}
                onChange={(e) => setFormData({ ...formData, work_description: e.target.value })}
                rows={4}
                required
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes / Notas Adicionales (Optional / Opcional)</Label>
              <Textarea
                id="notes"
                data-testid="form-notes-input"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>

            {/* Signature */}
            <div className="space-y-2">
              <Label htmlFor="signature">Signature / Firma (Full Name / Nombre Completo)</Label>
              <Input
                id="signature"
                data-testid="form-signature-input"
                value={formData.signature}
                onChange={(e) => setFormData({ ...formData, signature: e.target.value })}
                placeholder="Type your full name / Escriba su nombre completo"
                required
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              data-testid="submit-form-btn"
              style={{ backgroundColor: '#6B9F3E' }}
            >
              <Send className="h-4 w-4 mr-2" />
              {loading ? 'Submitting... / Enviando...' : 'Submit Form / Enviar Formulario'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default FormSubmission;